import { useCallback, useEffect, useState } from 'react';

/**
 * Light/dark theme. The preference lives in localStorage; the resolved theme is `data-theme` on <html>, which selects
 * the dark token block generated in theme/tokens.css. public/theme-init.js applies the same rule before the first
 * paint (inlined into index.html at build time and allowed by the CSP through its SHA-256), so keep both in step.
 */
export type ThemePreference = 'light' | 'dark' | 'system';
export type Theme = 'light' | 'dark';

export const THEME_KEY = 'ana.theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

export function readPreference(): ThemePreference {
  try {
    const v = localStorage.getItem(THEME_KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}

export function writePreference(pref: ThemePreference): void {
  try {
    localStorage.setItem(THEME_KEY, pref);
  } catch {
    /* storage unavailable (private mode): the choice lasts until the page is closed */
  }
}

export function systemPrefersDark(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches;
}

export function resolveTheme(pref: ThemePreference, systemDark: boolean): Theme {
  return pref === 'system' ? (systemDark ? 'dark' : 'light') : pref;
}

/**
 * The header offers a single toggle. Choosing the theme the system already uses goes back to following the system,
 * so a later change of the operating-system setting is honoured again instead of being pinned forever.
 */
export function nextPreference(current: Theme, systemDark: boolean): ThemePreference {
  const target: Theme = current === 'dark' ? 'light' : 'dark';
  return target === (systemDark ? 'dark' : 'light') ? 'system' : target;
}

export function applyTheme(theme: Theme): void {
  document.documentElement.dataset.theme = theme;
}

export function useTheme(): { theme: Theme; preference: ThemePreference; toggle: () => void } {
  const [preference, setPreference] = useState<ThemePreference>(readPreference);
  const [systemDark, setSystemDark] = useState<boolean>(systemPrefersDark);
  const theme = resolveTheme(preference, systemDark);

  useEffect(() => applyTheme(theme), [theme]);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(DARK_QUERY);
    const onChange = () => setSystemDark(mq.matches);
    mq.addEventListener('change', onChange);
    // Another tab changed the preference: follow it, so two open tabs never disagree.
    const onStorage = (e: StorageEvent) => {
      if (e.key === THEME_KEY || e.key === null) setPreference(readPreference());
    };
    window.addEventListener('storage', onStorage);
    return () => {
      mq.removeEventListener('change', onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const toggle = useCallback(() => {
    const next = nextPreference(theme, systemDark);
    writePreference(next);
    setPreference(next);
  }, [theme, systemDark]);

  return { theme, preference, toggle };
}
